// Vulnerability disclosure and security contact.
//
// The contact comes from the operator record, so this page can never leak
// a private mailbox that was never meant to be published.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";

import { api } from "@/lib/api";
import { useOperatorContacts } from "@/lib/operatorContacts";
import CachePublicShell from "@/components/CachePublicShell";
import SEO from "@/components/SEO";

export default function SecurityContact() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const contacts = useOperatorContacts();

  useEffect(() => {
    api.get("/legal/security")
      .then((r) => setData(r.data))
      .catch(() => setFailed(true));
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14"
         data-testid="security-contact">
      <CachePublicShell />
      <SEO title="Security — ConfirmPH"
           description="How to report a security vulnerability or suspected data exposure to
                        ConfirmPH." />
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
        ConfirmPH · Security
      </p>
      <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2">
        Report a security issue
      </h1>
      <p className="mt-4 text-slate-700 leading-relaxed">
        If you have found a security vulnerability in ConfirmPH, believe an account has been
        compromised, or have seen data that looks exposed, please tell us. Reports are read and
        triaged by a person.
      </p>

      {!data && !failed && (
        <p className="mt-8 flex items-center gap-2 text-sm text-slate-500" role="status">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </p>
      )}
      {failed && (
        <p className="mt-8 text-sm text-slate-600" data-testid="security-contact-error">
          The security page could not be loaded right now. Please write to{" "}
          <a href={`mailto:${contacts.security}`} className="underline break-all">
            {contacts.security}
          </a>.
        </p>
      )}

      {data && (
        <>
          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4"
               data-testid="security-contact-address">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-slate-500" />
              <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                Security contact
              </span>
            </div>
            {data.contact ? (
              <>
                <a href={`mailto:${data.contact}`}
                   className="mt-1 block text-lg font-medium text-slate-900 underline break-all">
                  {data.contact}
                </a>
                {data.contact_kind === "support" && (
                  <p className="mt-1 text-xs text-slate-600" data-testid="security-contact-kind">
                    A dedicated security address has not been set up yet, so security reports
                    go to the monitored support inbox. Put “Security” in the subject line.
                  </p>
                )}
              </>
            ) : (
              <p className="mt-1 text-sm text-slate-700">
                No security contact address is configured yet.
              </p>
            )}
          </div>

          <section className="mt-8" data-testid="security-report-types">
            <h2 className="font-display text-xl font-semibold">What to report</h2>
            <ul className="mt-3 space-y-1.5 text-sm text-slate-800 list-disc pl-5">
              {data.report_types.map((type) => (
                <li key={type.key}>{type.label}</li>
              ))}
            </ul>
          </section>

          {Array.isArray(data.not_for) && data.not_for.length > 0 && (
            <section className="mt-8" data-testid="security-not-for">
              <h2 className="font-display text-xl font-semibold">Not for this route</h2>
              <p className="mt-2 text-sm text-slate-700">
                This route is only for security vulnerabilities and incidents. For anything else,
                use the right route so it reaches the right people:
              </p>
              <ul className="mt-3 space-y-1.5 text-sm text-slate-800 list-disc pl-5">
                {data.not_for.map((row) => (
                  <li key={row.key}>
                    {row.label}{" "}
                    <Link to={row.route} className="underline">(go there)</Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="mt-8">
            <h2 className="font-display text-xl font-semibold">What we ask</h2>
            <p className="mt-2 text-sm text-slate-800">{data.commitment}</p>
            {data.safe_harbour_note && (
              <p className="mt-2 text-sm text-slate-700" data-testid="security-safe-harbour">
                {data.safe_harbour_note}
              </p>
            )}
          </section>

          {data.policy_published && (
            <p className="mt-6 text-sm">
              <Link to="/legal/policies/vulnerability_disclosure" className="underline"
                    data-testid="security-policy-link">
                Read the full Vulnerability Disclosure Policy (v{data.policy_version})
              </Link>
            </p>
          )}
        </>
      )}

      <div className="mt-12 pt-6 border-t border-slate-200 text-sm text-slate-600 space-y-2">
        <p>
          Not a security issue?{" "}
          <Link to="/report" className="underline" data-testid="security-report-link">
            Report something else
          </Link>
        </p>
        <Link to="/" className="underline block">← Back to home</Link>
      </div>
    </div>
  );
}
